import React, { useState, useRef, useEffect } from 'react';
import { Map, TileLayer, Polygon, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { HexColorPicker } from 'react-colorful';
import Menu from '@components/Menu';
import useZones from '../../hooks/useZones';

// Cores predefinidas para as zonas
const PREDEFINED_COLORS = [
  { name: 'Vermelho', hex: '#FF0000' },
  { name: 'Verde', hex: '#00FF00' },
  { name: 'Azul', hex: '#0000FF' },
  { name: 'Amarelo', hex: '#FFD700' },
  { name: 'Laranja', hex: '#FFA500' },
  { name: 'Roxo', hex: '#800080' },
  { name: 'Rosa', hex: '#FF69B4' },
  { name: 'Castanho', hex: '#8B4513' },
  { name: 'Cinzento', hex: '#808080' },
  { name: 'Preto', hex: '#000000' },
  { name: 'Branco', hex: '#FFFFFF' },
  { name: 'Turquesa', hex: '#40E0D0' }
];

// Fix para os ícones do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Array de cores para as zonas
const ZONE_COLORS = [
  { fill: '#3498db', stroke: '#2980b9', label: '#2c3e50' }, // Azul
  { fill: '#e74c3c', stroke: '#c0392b', label: '#ffffff' }, // Vermelho
  { fill: '#2ecc71', stroke: '#27ae60', label: '#ffffff' }, // Verde
  { fill: '#f39c12', stroke: '#e67e22', label: '#ffffff' }, // Laranja
  { fill: '#9b59b6', stroke: '#8e44ad', label: '#ffffff' }, // Roxo
  { fill: '#1abc9c', stroke: '#16a085', label: '#ffffff' }, // Turquesa
  { fill: '#e67e22', stroke: '#d35400', label: '#ffffff' }, // Laranja escuro
  { fill: '#34495e', stroke: '#2c3e50', label: '#ffffff' }, // Cinza escuro
  { fill: '#e91e63', stroke: '#c2185b', label: '#ffffff' }, // Rosa
  { fill: '#607d8b', stroke: '#455a64', label: '#ffffff' }, // Azul cinza
];

// Função para obter cor da zona baseada no ID
const getZoneColor = (zoneId) => {
  const colorIndex = zoneId % ZONE_COLORS.length;
  return ZONE_COLORS[colorIndex].fill;
};

// Função para converter hex para RGB
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Componente de seleção de cor
const ColorPickerSection = ({ color, onChange }) => {
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  return (
    <div className="color-picker-section">
      <label className="color-picker-label">Cor da Zona</label>
      <div className="color-picker-grid">
        {PREDEFINED_COLORS.map((presetColor) => (
          <button
            key={presetColor.hex}
            onClick={(e) => {
              e.stopPropagation();
              onChange(presetColor.hex);
              setShowCustomPicker(false);
            }}
            className={`color-swatch ${color === presetColor.hex ? 'active' : ''}`}
            style={{ backgroundColor: presetColor.hex }}
            title={presetColor.name}
          />
        ))}
      </div>
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          setShowCustomPicker(!showCustomPicker);
        }}
        className="btn btn-secondary color-picker-toggle"
      >
        🎨 {showCustomPicker ? 'Esconder Seletor' : 'Cor Personalizada'}
      </button>
      
      {showCustomPicker && (
        <div className="custom-color-picker" onClick={(e) => e.stopPropagation()}>
          <div onClick={(e) => e.stopPropagation()}>
            <HexColorPicker color={color} onChange={onChange} />
          </div>
          <div className="color-input-group">
            <div 
              className="color-preview"
              style={{ backgroundColor: color }}
            />
            <input
              type="text"
              value={color}
              onChange={(e) => {
                e.stopPropagation();
                onChange(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              className="form-control color-input"
              pattern="^#[0-9A-Fa-f]{6}$"
              placeholder="#000000"
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Função para criar ícone personalizado com texto
const createTextIcon = (text, color) => {
  return L.divIcon({
    className: 'custom-text-icon',
    html: `<div class="zone-label" style="background-color: ${color.stroke}; color: ${color.label}; border-color: ${color.fill};">${text}</div>`,
    iconSize: [120, 30],
    iconAnchor: [60, 15]
  });
};

// Hook para adicionar event listeners ao mapa
function useMapEvents(mapRef, isDrawingMode, onPointAdded, onPolygonCompleted, polygons, onZoneClick) {
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.leafletElement;
    
    // Função para verificar se um ponto está dentro de um polígono
    const isPointInPolygon = (point, polygon) => {
      const x = point[0];
      const y = point[1];
      let inside = false;
      
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0];
        const yi = polygon[i][1];
        const xj = polygon[j][0];
        const yj = polygon[j][1];
        
        if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
          inside = !inside;
        }
      }
      
      return inside;
    };
    
    const handleClick = (e) => {
      const { lat, lng } = e.latlng;
      
      // Verificar se o clique foi em elementos de controlo
      const target = e.originalEvent.target;
      const isControlElement = target.closest('.control-panel') || 
                              target.closest('.zones-list') || 
                              target.closest('.navigation-buttons') ||
                              target.closest('.modal-overlay');
      
      if (isControlElement) {
        return; // Não processar cliques nos controlos
      }
      
      if (isDrawingMode) {
        onPointAdded([lat, lng]);
      } else {
        // Verificar se o clique foi dentro de uma zona existente
        const clickPoint = [lat, lng];
        const clickedZones = polygons.filter(zone => 
          isPointInPolygon(clickPoint, zone.coordinates)
        );
        
        if (clickedZones.length > 0) {
          // Se há múltiplas zonas sobrepostas, escolher a menor (mais específica)
          const mostSpecificZone = clickedZones.reduce((smallest, current) => {
            const currentArea = current.area || 0;
            const smallestArea = smallest.area || 0;
            return currentArea < smallestArea ? current : smallest;
          });
          
          onZoneClick(mostSpecificZone);
        }
      }
    };

    const handleDblClick = (e) => {
      if (isDrawingMode) {
        const { lat, lng } = e.latlng;
        onPointAdded([lat, lng]);
      }
    };

    map.on('click', handleClick);
    map.on('dblclick', handleDblClick);

    return () => {
      map.off('click', handleClick);
      map.off('dblclick', handleDblClick);
    };
  }, [mapRef, isDrawingMode, onPointAdded, onPolygonCompleted, polygons, onZoneClick]);
}

// Componente para controlar o zoom e desenho
function MapController({ onZoneCreated, isDrawing, onDrawingChange, mapRef, zones, onZoneUpdate, onZoneDelete, onMultipleZonesDelete, onHelpClick, onGetUserLocation }) {
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [showNameModal, setShowNameModal] = useState(false);
  const [zoneName, setZoneName] = useState('');
  const [pendingPolygon, setPendingPolygon] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [polygonHistory, setPolygonHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const startDrawing = (e) => {
    e.stopPropagation();
    setIsDrawingMode(true);
    onDrawingChange(true);
    setCurrentPolygon([]);
    setPolygonHistory([[]]);
    setHistoryIndex(0);
  };

  const cancelDrawing = (e) => {
    e.stopPropagation();
    setIsDrawingMode(false);
    onDrawingChange(false);
    setCurrentPolygon([]);
    setPolygonHistory([]);
    setHistoryIndex(-1);
  };

  const clearAllZones = async (e) => {
    e.stopPropagation();
    if (zones.length === 0) return;
    
    if (window.confirm(`Tem a certeza que deseja eliminar todas as ${zones.length} zonas?`)) {
      try {
        const zoneIds = zones.map(zone => zone.id);
        await onMultipleZonesDelete(zoneIds);
      } catch (err) {
        console.error('Erro ao eliminar todas as zonas:', err);
      }
    }
  };

  const handleDeleteZone = async (zoneId, e) => {
    e.stopPropagation();
    try {
      await onZoneDelete(zoneId);
    } catch (err) {
      console.error('Erro ao eliminar zona:', err);
    }
  };

  const handlePointAdded = (point) => {
    setCurrentPolygon(prev => {
      // Evitar adicionar pontos muito próximos (menos de 10 metros)
      if (prev.length > 0) {
        const lastPoint = prev[prev.length - 1];
        const distance = Math.sqrt(
          Math.pow(point[0] - lastPoint[0], 2) + 
          Math.pow(point[1] - lastPoint[1], 2)
        );
        
        // Se a distância for muito pequena, não adicionar o ponto
        if (distance < 0.0001) {
          return prev;
        }
      }
      
      const newPolygon = [...prev, point];
      
      // Adicionar ao histórico
      setPolygonHistory(prevHistory => {
        const newHistory = prevHistory.slice(0, historyIndex + 1);
        newHistory.push([...newPolygon]);
        return newHistory;
      });
      setHistoryIndex(prevIndex => prevIndex + 1);
      
      return newPolygon;
    });
  };

  const handlePolygonCompleted = () => {
    if (currentPolygon.length >= 3) {
      // Preparar polígono para nomeação
      const newPolygon = [...currentPolygon, currentPolygon[0]]; // Fechar o polígono
      setPendingPolygon(newPolygon);
      setSelectedColor(null); // Será definida automaticamente
      setShowNameModal(true);
    }
  };

  const handleSaveZone = async (e) => {
    e.stopPropagation();
    if (zoneName.trim()) {
      try {
        if (editingZone) {
          // Editar zona existente
          const updatedZone = {
            ...editingZone,
            name: zoneName.trim(),
            color: selectedColor || editingZone.color
          };
          await onZoneUpdate(updatedZone);
          setEditingZone(null);
        } else if (pendingPolygon) {
          // Criar nova zona
          const zoneId = Date.now();
          const selectedColorHex = selectedColor || '#3498db';
          const newZone = {
            id: zoneId,
            coordinates: pendingPolygon,
            name: zoneName.trim(),
            createdAt: new Date().toISOString(),
            area: calculatePolygonArea(pendingPolygon),
            center: calculatePolygonCenter(pendingPolygon),
            color: selectedColorHex
          };
          
          setCurrentPolygon([]);
          setIsDrawingMode(false);
          onDrawingChange(false);
          onZoneCreated(newZone);
        }
        
        setShowNameModal(false);
        setZoneName('');
        setPendingPolygon(null);
      } catch (err) {
        console.error('Erro ao guardar zona:', err);
      }
    }
  };

  const handleCancelZone = (e) => {
    e.stopPropagation();
    setShowNameModal(false);
    setZoneName('');
    setPendingPolygon(null);
    setEditingZone(null);
    setSelectedColor(null);
    setCurrentPolygon([]);
    setIsDrawingMode(false);
    onDrawingChange(false);
  };

  const handleZoneClick = (zone) => {
    setEditingZone(zone);
    setZoneName(zone.name);
    setSelectedColor(zone.color || getZoneColor(zone.id));
    setShowNameModal(true);
  };

  const undoLastPoint = (e) => {
    e.stopPropagation();
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...polygonHistory[newIndex]]);
    }
  };

  const redoNextPoint = (e) => {
    e.stopPropagation();
    if (historyIndex < polygonHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...polygonHistory[newIndex]]);
    }
  };

  // Função para calcular área aproximada do polígono em km²
  const calculatePolygonArea = (coordinates) => {
    if (!coordinates || coordinates.length < 3) return 0;
    
    
    // Usar a fórmula de Shoelace para calcular área em graus quadrados
    let area = 0;
    const n = coordinates.length - 1; // Excluir o último ponto (duplicado)
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      // coordinates[i] = [lat, lng]
      area += coordinates[i][1] * coordinates[j][0]; // lng * lat
      area -= coordinates[j][1] * coordinates[i][0]; // lng * lat
    }
    
    const areaInDegrees = Math.abs(area) / 2;
    
    // Se a área for muito pequena, pode ser um problema de precisão
    if (areaInDegrees < 1e-10) {
      return 0;
    }
    
    // Calcular a latitude média do polígono
    let latSum = 0;
    for (let i = 0; i < n; i++) {
      latSum += coordinates[i][0]; // latitude
    }
    const avgLat = latSum / n;
    const latRad = avgLat * Math.PI / 180;
    
    // Conversão correta: 1 grau de latitude = 111.32 km
    // 1 grau de longitude = 111.32 * cos(latitude) km
    const kmPerDegreeLat = 111.32;
    const kmPerDegreeLng = 111.32 * Math.cos(latRad);
    
    // Área em km² = área em graus quadrados × km/grau lat × km/grau lng
    const areaInKm2 = areaInDegrees * kmPerDegreeLat * kmPerDegreeLng;
    
    
    return areaInKm2;
  };

  // Função para calcular o centro geométrico de um polígono
  const calculatePolygonCenter = (coordinates) => {
    if (coordinates.length < 3) return [0, 0];
    
    let lat = 0;
    let lng = 0;
    const n = coordinates.length - 1; // Excluir o último ponto (duplicado)
    
    for (let i = 0; i < n; i++) {
      lat += coordinates[i][0];
      lng += coordinates[i][1];
    }
    
    return [lat / n, lng / n];
  };

  // Usar o hook para adicionar event listeners
  useMapEvents(mapRef, isDrawingMode, handlePointAdded, handlePolygonCompleted, zones, handleZoneClick);

  return (
    <div className="map-controls">
      <div className="control-panel" onClick={(e) => e.stopPropagation()}>
        <h3>Controlos do Mapa</h3>
        <div className="control-buttons">
          {!isDrawingMode ? (
            <>
              <button onClick={startDrawing} className="btn btn-primary">
                🖊️ Desenhar Zona
              </button>
              {zones.length > 0 && (
                <button onClick={clearAllZones} className="btn btn-danger">
                  🗑️ Apagar Todas
                </button>
              )}
              <button onClick={onGetUserLocation} className="btn btn-secondary">
                📍 Minha Localização
              </button>
              <button onClick={onHelpClick} className="btn btn-info">
                ❓ Ajuda
              </button>
            </>
          ) : (
            <>
              <button onClick={cancelDrawing} className="btn btn-secondary">
                ❌ Cancelar Desenho
              </button>
              
              {/* Botões de navegação */}
              <div className="navigation-buttons" onClick={(e) => e.stopPropagation()}>
                <button 
                  onClick={undoLastPoint}
                  className="btn btn-warning"
                  disabled={historyIndex <= 0}
                  title="Remover último ponto"
                >
                  ↺ Desfazer
                </button>
                <button 
                  onClick={redoNextPoint}
                  className="btn btn-info"
                  disabled={historyIndex >= polygonHistory.length - 1}
                  title="Refazer ponto removido"
                >
                  ↻ Refazer
                </button>
              </div>
              
              {currentPolygon.length >= 3 && (
                <button onClick={(e) => { e.stopPropagation(); handlePolygonCompleted(); }} className="btn btn-success">
                  ✅ Finalizar Zona
                </button>
              )}
            </>
          )}
        </div>

      </div>

      {/* Modal para nomear/editar a zona */}
      {showNameModal && (
        <div className="modal-overlay">
          <div className="modal-content zone-modal">
            <h3>{editingZone ? 'Editar Zona' : 'Criar Zona'}</h3>
            <p>{editingZone ? 'Edite o nome e cor da zona:' : 'Configure o nome e cor da zona:'}</p>
            
            <div className="zone-form">
              <div className="form-group">
                <label>Nome da Zona</label>
                <input
                  type="text"
                  value={zoneName}
                  onChange={(e) => setZoneName(e.target.value)}
                  placeholder="Ex: Zona Norte, Área Comercial, etc."
                  className="zone-name-input"
                  autoFocus
                />
              </div>
              
              <ColorPickerSection
                color={selectedColor || (editingZone ? editingZone.color : '#3498db')}
                onChange={setSelectedColor}
              />
            </div>
            <div className="modal-buttons">
              <button 
                onClick={handleSaveZone}
                className="btn btn-primary"
                disabled={!zoneName.trim()}
              >
                {editingZone ? '✏️ Atualizar Nome' : '💾 Guardar Zona'}
              </button>
              <button 
                onClick={handleCancelZone}
                className="btn btn-secondary"
              >
                ❌ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Renderizar polígonos existentes */}
      {zones.map(zone => {
        // Se zone.color é uma string (hex), usar diretamente
        // Se é um objeto, usar as propriedades fill/stroke
        let zoneColor;
        if (typeof zone.color === 'string') {
          // Converter hex para objeto com fill e stroke
          zoneColor = {
            fill: zone.color,
            stroke: zone.color,
            label: '#ffffff'
          };
        } else {
          zoneColor = zone.color || getZoneColor(zone.id);
        }
        
        return (
          <div key={zone.id}>
            <Polygon
              positions={zone.coordinates}
              color={zoneColor.stroke}
              fillColor={zoneColor.fill}
              fillOpacity={0.3}
              weight={2}
            />
            {/* Marker com nome da zona no centro */}
            <Marker 
              position={zone.center} 
              icon={createTextIcon(zone.name, zoneColor)}
            />
          </div>
        );
      })}

      {/* Renderizar polígono em construção */}
      {currentPolygon.length > 0 && (
        <Polygon
          positions={currentPolygon}
          color="red"
          fillColor="red"
          fillOpacity={0.2}
          weight={2}
          dashArray="5, 5"
        />
      )}
    </div>
  );
}

// Componente principal do mapa
function PortugalMap({ profile, location, history }) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const mapRef = useRef(null);
  
  // Obter idempresa do perfil do utilizador
  let idempresa = parseInt(profile?.idempresa || profile?.member_idempresa || 0);
  
  // Fallback se idempresa for inválido
  if (isNaN(idempresa) || idempresa <= 0) {
    idempresa = 1; // Fallback para teste
  }

  // Função para obter a localização do utilizador
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation([latitude, longitude]);
        
        // Centrar o mapa na localização do utilizador
        if (mapRef.current) {
          const map = mapRef.current.leafletElement;
          map.setView([latitude, longitude], 13);
        }
      },
      (error) => {
        console.error('Erro ao obter localização:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutos
      }
    );
  };
  
  // Usar o hook de zonas
  const { 
    zones, 
    loading, 
    error, 
    addZone, 
    updateZone, 
    deleteZone, 
    deleteMultipleZones,
    syncZone,
    syncMultipleZones,
    loadZones
  } = useZones(idempresa);

  const handleZoneCreated = async (zoneData) => {
    try {
      // Sincronizar com a base de dados
      await syncZone(zoneData);
    } catch (err) {
      console.error('Erro ao sincronizar zona:', err);
      // A zona já foi adicionada localmente pelo MapController
    }
  };

  const handleDrawingChange = (drawing) => {
    setIsDrawing(drawing);
  };

  // Função para exportar dados
  const exportZones = () => {
    return zones;
  };

  // Função para sincronizar todas as zonas locais
  const syncAllZones = async () => {
    try {
      // Aqui poderia implementar lógica para sincronizar zonas locais
      // Por agora, apenas recarregar da API
      await loadZones();
    } catch (err) {
      console.error('Erro ao sincronizar zonas:', err);
    }
  };

  // Função para eliminar uma zona individual
  const handleDeleteZone = async (zoneId, e) => {
    e.stopPropagation();
    
    if (window.confirm('Tem a certeza que deseja eliminar esta zona?')) {
      try {
        await deleteZone(zoneId);
      } catch (err) {
        console.error('Erro ao eliminar zona:', err);
      }
    }
  };

  // Função para verificar se uma zona tem prioridade sobre outras (é mais específica)
  const isZonePrioritized = (zone) => {
    if (!zone.area) return false;
    
    // Verificar se há outras zonas que contêm esta zona
    const containingZones = zones.filter(otherZone => {
      if (otherZone.id === zone.id || !otherZone.area) return false;
      
      // Se a outra zona é maior e contém o centro desta zona
      return otherZone.area > zone.area && 
             isPointInPolygon(zone.center, otherZone.coordinates);
    });
    
    return containingZones.length > 0;
  };

  // Função para verificar se um ponto está dentro de um polígono (reutilizada)
  const isPointInPolygon = (point, polygon) => {
    const x = point[0];
    const y = point[1];
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Função para centrar uma zona no mapa
  const handleZoneClick = (zone, e) => {
    e.stopPropagation();
    
    if (mapRef.current && zone.center) {
      const map = mapRef.current.leafletElement;
      map.setView(zone.center, 15); // Zoom 15 para mostrar a zona detalhadamente
      
    }
  };

  // Função para centrar zona quando clicada no preview (painel colapsado)
  const handleZonePreviewClick = (zone) => {
    if (mapRef.current) {
      const map = mapRef.current.leafletElement;
      
      if (zone.center) {
        // Se a zona já tem centro calculado, usar diretamente
        map.setView(zone.center, 15);
      } else if (zone.coordinates && zone.coordinates.length > 0) {
        // Calcular o centro do polígono
        let latSum = 0;
        let lngSum = 0;
        const validCoords = zone.coordinates.filter(coord => coord && coord.length >= 2);
        
        if (validCoords.length > 0) {
          validCoords.forEach(coord => {
            latSum += coord[0];
            lngSum += coord[1];
          });
          
          const centerLat = latSum / validCoords.length;
          const centerLng = lngSum / validCoords.length;
          
          // Centrar o mapa na zona
          map.setView([centerLat, centerLng], 15);
        }
      }
    }
  };

  // Função para alternar sidebar
  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  // Função para abrir/fechar modal de ajuda
  const toggleHelpModal = () => {
    setShowHelpModal(!showHelpModal);
  };


  // Efeito para carregar CSS dinamicamente e adicionar classe ao body
  useEffect(() => {
    // Carregar CSS dinamicamente
    const loadCSS = async () => {
      try {
        await import('./zones.css');
      } catch (error) {
        console.error('Erro ao carregar CSS das zonas:', error);
      }
    };

    loadCSS();

    // Adicionar classe ao body
    document.body.classList.add('map-page');
    
    return () => {
      // Remover classe do body
      document.body.classList.remove('map-page');
    };
  }, []);

  // Obter localização do utilizador ao carregar o componente
  useEffect(() => {
    getUserLocation();
  }, []);

  // Forçar atualização do mapa quando a sidebar expandir/colapsar
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.leafletElement;
      // Pequeno delay para permitir que o CSS seja aplicado
      setTimeout(() => {
        map.invalidateSize();
      }, 300);
    }
  }, [sidebarExpanded]);

  // Forçar inicialização do mapa após carregamento
  useEffect(() => {
    const timer = setTimeout(() => {
      if (mapRef.current) {
        const map = mapRef.current.leafletElement;
        map.invalidateSize();
        // Forçar re-render do mapa
        map.setView(map.getCenter(), map.getZoom());
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Expor funções globalmente para debug (apenas em desenvolvimento)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      window.exportZones = exportZones;
      window.zones = zones;
      window.syncAllZones = syncAllZones;
      window.idempresa = idempresa;
      window.getUserLocation = getUserLocation;
    }
  }, [zones, idempresa]);


  return (
    <div>
      <Menu location={location} history={history} />
      <div className="fullContainer" style={{ maxWidth: '100%' }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: 'left',
              marginTop: '75px',
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li>
              <a href="/">Ferramentas</a>
            </li>
            <li className="active">Mapa de Zonas</li>
          </ol>
        </div>
      </div>

      <div className="portugal-map-container">
        <div className="map-header">
          <h1>Mapa - Gestão de Zonas</h1>
          <p>Use os controlos para desenhar e gerir zonas</p>
        </div>

        <div className="map-layout">
          {/* Zones Panel */}
          <div className={`zones-panel ${sidebarExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="zones-panel-header" onClick={toggleSidebar} title={sidebarExpanded ? 'Colapsar painel' : 'Expandir painel'}>
              <h3>{sidebarExpanded ? `Zonas Criadas (${zones.length})` : 'Zonas'}</h3>
              <div className="zones-panel-toggle">
                {sidebarExpanded ? '◀' : '▶'}
              </div>
            </div>
            
            {!sidebarExpanded && (
              <div className="zones-panel-collapsed-content">
                <div className="zones-count">
                  <span className="count-number">{zones.length}</span>
                  <span className="count-label">Zonas</span>
                </div>
                
                {zones.length > 0 && (
                  <div className="zones-summary">
                    <div className="summary-item">
                      <span className="summary-icon">📊</span>
                      <span className="summary-text">
                        {zones.reduce((total, zone) => total + (zone.area || 0), 0).toFixed(1)} km²
                      </span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-icon">📍</span>
                      <span className="summary-text">
                        {zones.reduce((total, zone) => total + (zone.coordinates.length - 1), 0)} pontos
                      </span>
                    </div>
                  </div>
                )}
                
                {loading && (
                  <div className="loading-indicator-small">
                    <div className="spinner-small"></div>
                  </div>
                )}
                
                {error && (
                  <div className="error-indicator-small">
                    <span>⚠️</span>
                  </div>
                )}
                
           <div className="zones-preview">
             {zones.map(zone => {
               // Se zone.color é uma string (hex), usar diretamente
               // Se é um objeto, usar as propriedades fill/stroke
               let zoneColor;
               if (typeof zone.color === 'string') {
                 zoneColor = {
                   fill: zone.color,
                   stroke: zone.color,
                   label: '#ffffff'
                 };
               } else {
                 zoneColor = zone.color || getZoneColor(zone.id);
               }
               
               return (
                 <div 
                   key={zone.id}
                   className="zone-preview-item"
                   style={{ borderLeftColor: zoneColor.fill }}
                   title={`Clique para centrar: ${zone.name}`}
                   onClick={() => handleZonePreviewClick(zone)}
                 >
                   <div 
                     className="zone-preview-color" 
                     style={{ backgroundColor: zoneColor.fill }}
                   ></div>
                   <span className="zone-preview-name">{zone.name}</span>
                 </div>
               );
             })}
           </div>
              </div>
            )}
            
            {sidebarExpanded && (
              <div className="zones-panel-content">
                {!idempresa && (
                  <div className="error-message">
                    <p>⚠️ ID da empresa não encontrado. Verifique se está autenticado.</p>
                    <p><strong>ID Empresa:</strong> {idempresa}</p>
                  </div>
                )}

                {/* Status da localização */}
                {userLocation && (
                  <div className="success-message">
                    <p>✅ Localização obtida com sucesso</p>
                    <small>Lat: {userLocation[0].toFixed(4)}, Lng: {userLocation[1].toFixed(4)}</small>
                  </div>
                )}
                
                {loading && (
                  <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>A carregar zonas...</p>
                  </div>
                )}
                
                {error && (
                  <div className="error-message">
                    <p>❌ {error}</p>
                    <button 
                      onClick={() => window.location.reload()} 
                      className="btn btn-sm btn-secondary"
                    >
                      Tentar Novamente
                    </button>
                  </div>
                )}
                
                {!loading && !error && zones.length > 0 ? (
                  <div className="zones-info-list">
                    {zones.map(zone => {
                      // Se zone.color é uma string (hex), usar diretamente
                      // Se é um objeto, usar as propriedades fill/stroke
                      let zoneColor;
                      if (typeof zone.color === 'string') {
                        zoneColor = {
                          fill: zone.color,
                          stroke: zone.color,
                          label: '#ffffff'
                        };
                      } else {
                        zoneColor = zone.color || getZoneColor(zone.id);
                      }
                      
                      const rgb = hexToRgb(zoneColor.fill);
                      const rgbString = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '0, 123, 255';
                      
                      const isPrioritized = isZonePrioritized(zone);
                      
                      return (
                        <div 
                          key={zone.id} 
                          className={`zone-info-item clickable-zone ${isPrioritized ? 'zone-prioritized' : ''}`}
                          style={{ 
                            borderLeftColor: zoneColor.fill,
                            '--zone-color': zoneColor.fill,
                            '--zone-stroke': zoneColor.stroke,
                            '--zone-color-rgb': rgbString
                          }}
                          onClick={(e) => handleZoneClick(zone, e)}
                          title={`Clicar para centrar no mapa${isPrioritized ? ' (Zona prioritária - mais específica)' : ''}`}
                        >
                          <div className="zone-info-header">
                            <div 
                              className="zone-color-indicator" 
                              style={{ backgroundColor: zoneColor.fill }}
                            ></div>
                            <span className="zone-name">
                              {zone.name}
                              {isPrioritized && (
                                <span 
                                  className="priority-indicator" 
                                  title="Zona prioritária"
                                  role="img"
                                  aria-label="Zona prioritária"
                                >
                                  ⭐
                                </span>
                              )}
                            </span>
                            <button
                              onClick={(e) => handleDeleteZone(zone.id, e)}
                              className="btn btn-sm btn-danger zone-delete-btn"
                              title="Eliminar zona"
                            >
                              🗑️
                            </button>
                          </div>
                          <small className="zone-details">
                            {zone.coordinates.length - 1} pontos • Área: {zone.area.toFixed(2)} km²
                            {isPrioritized && <span className="priority-text"> • Prioritária</span>}
                          </small>
                        </div>
                      );
                    })}
                  </div>
                ) : !loading && !error ? (
                  <p>Nenhuma zona criada ainda.</p>
                ) : null}
                
                <div style={{ marginTop: '15px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button 
                    onClick={(e) => { e.stopPropagation(); exportZones(); }}
                    className="btn btn-primary"
                    style={{ flex: 1, minWidth: '120px' }}
                    disabled={loading}
                  >
                    📊 Exportar Dados
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); syncAllZones(); }}
                    className="btn btn-secondary"
                    style={{ flex: 1, minWidth: '120px' }}
                    disabled={loading}
                    title="Sincronizar com a base de dados"
                  >
                    🔄 Sincronizar
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="map-wrapper">
          <Map
            center={userLocation || [39.5, -8.0]} // Centro de Portugal ou localização do utilizador
            zoom={userLocation ? 13 : 7} // Zoom maior se tiver localização do utilizador
            style={{ height: '100%', width: '100%' }}
            zoomControl={true}
            ref={mapRef}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <MapController 
              onZoneCreated={handleZoneCreated}
              isDrawing={isDrawing}
              onDrawingChange={handleDrawingChange}
              mapRef={mapRef}
              zones={zones}
              onZoneUpdate={updateZone}
              onZoneDelete={deleteZone}
              onMultipleZonesDelete={deleteMultipleZones}
              onHelpClick={toggleHelpModal}
              onGetUserLocation={getUserLocation}
            />
          </Map>
        </div>
        </div>

        {/* Modal de Ajuda */}
        {showHelpModal && (
          <div className="modal-overlay help-modal-overlay" onClick={toggleHelpModal}>
            <div className="modal-content help-modal" onClick={(e) => e.stopPropagation()}>
              <div className="help-modal-header">
                <div className="help-modal-icon">❓</div>
                <div className="help-modal-title">
                  <h3>Ajuda - Mapa de Zonas</h3>
                  <p>Guia completo para usar o sistema de zonas</p>
                </div>
                <button 
                  onClick={toggleHelpModal}
                  className="help-modal-close"
                  title="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div className="help-modal-body">
                <div className="help-grid">
                  <div className="help-card">
                    <div className="help-card-icon">📍</div>
                    <div className="help-card-content">
                      <h4>Navegação</h4>
                      <ul>
                        <li><span className="help-key">Minha Localização</span> - Centra o mapa na sua posição</li>
                        <li><span className="help-key">Clique no card</span> - Centra a zona no mapa</li>
                        <li><span className="help-key">Clique na zona</span> - Edita nome/cor da zona</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="help-card">
                    <div className="help-card-icon">🖊️</div>
                    <div className="help-card-content">
                      <h4>Desenhar Zonas</h4>
                      <ul>
                        <li><span className="help-key">Desenhar Zona</span> - Inicia modo de desenho</li>
                        <li><span className="help-key">Clique</span> no mapa para adicionar pontos</li>
                        <li><span className="help-key">Duplo clique</span> no mapa para adicionar pontos</li>
                        <li><span className="help-key">Desfazer</span> - Remove último ponto</li>
                        <li><span className="help-key">Refazer</span> - Restaura ponto removido</li>
                        <li><span className="help-key">Finalizar</span> - Completa a zona (mín. 3 pontos)</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="help-card">
                    <div className="help-card-icon">🎨</div>
                    <div className="help-card-content">
                      <h4>Personalização</h4>
                      <ul>
                        <li><span className="help-key">Cores</span> - Escolha cores para cada zona</li>
                        <li><span className="help-key">Nomes</span> - Nomeie as suas zonas</li>
                        <li><span className="help-key">Área</span> - Calculada automaticamente em km²</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="help-card">
                    <div className="help-card-icon">🗑️</div>
                    <div className="help-card-content">
                      <h4>Gestão</h4>
                      <ul>
                        <li><span className="help-key">Eliminar</span> - Botão 🗑️ em cada card</li>
                        <li><span className="help-key">Limpar Todas</span> - Remove todas as zonas</li>
                        <li><span className="help-key">Exportar</span> - Exporta para o console</li>
                        <li><span className="help-key">Sincronizar</span> - Sincroniza com a base de dados</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="help-modal-footer">
                <button 
                  onClick={toggleHelpModal}
                  className="btn btn-primary help-modal-btn"
                >
                  <span>✅</span> Entendi, obrigado!
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PortugalMap;
